/**
 * SCRUM-236 (DS-015 §13.8) — per-value-kind formatters for entity-change deltas.
 *
 * On the wire, `FieldChange.from` / `FieldChange.to` are JSON `unknown` (the
 * OpenAPI type is `{}`): Cents = number, Instant = epoch-ms number, enum =
 * string, time-of-day = "HH:mm" string, boolean, free text, opaque id, or a
 * structured value. Each formatter therefore takes `value: unknown` and is
 * **fail-closed**: it type-/finite-guards BEFORE any `intl.*` call or arithmetic
 * and returns a neutral marker (never throws, never leaks a raw object / internal
 * key to the user — gemini pre-plan B1).
 *
 * Money is display-only here (DS-015 / CLAUDE.md FE rule 6): Cents → major units
 * is the `/ 100` display-boundary conversion, not domain arithmetic. The currency
 * code is injected (the wire carries none reliably today); the host supplies the
 * real currency later, defaulting to USD.
 */
import type { IntlShape } from 'react-intl';
import { HISTORY_MESSAGE_IDS as H } from '../../i18n/message-ids.js';

/** The neutral, locale-safe marker for an undecodable / out-of-contract value. */
export function neutralValue(intl: IntlShape): string {
  return intl.formatMessage({ id: H.valueUnknown });
}

export type ValueKind =
  | 'enum'
  | 'cents'
  | 'instant'
  | 'isoInstant'
  | 'localDate'
  | 'timeOfDay'
  | 'count'
  | 'coordinate'
  | 'boolean'
  | 'text'
  | 'id'
  | 'json';

/** "HH:mm" 24-hour wall-clock, e.g. "15:00" / "09:30". */
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * A strict ISO-8601 datetime that carries an EXPLICIT zone (`Z` or a numeric
 * offset). Timezone-LESS strings (`2025-06-15T12:00:00`) are rejected: JS would
 * parse them in the host-local zone and then render them as UTC, making the
 * displayed time environment-dependent — the opposite of the UTC-anchored,
 * fail-closed guarantee. The real wire value is always `Z`-suffixed.
 */
const ISO_INSTANT_WITH_ZONE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;

/** Strict `YYYY-MM-DD` shape gate (a real calendar-date check follows). */
const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Money: Cents (integer) → localized currency. Non-finite-number ⇒ neutral. */
export function formatCents(value: unknown, intl: IntlShape, currency = 'USD'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return neutralValue(intl);
  // Display-boundary conversion only (no domain arithmetic on money).
  return intl.formatNumber(value / 100, { style: 'currency', currency });
}

/** Instant: epoch-ms number → localized date-time. UTC-anchored for determinism
 * (mirrors the SCRUM-172 ownershipDates doctrine — avoids timezone drift). */
export function formatInstant(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return neutralValue(intl);
  return intl.formatDate(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** ISO instant: an ISO-8601 datetime STRING (e.g. `'2025-06-15T12:00:00.000Z'`)
 * → localized UTC date-time. Unlike {@link formatInstant} (epoch-ms number), the
 * source here is a STRING. Fail-closed: non-string, or an unparseable string
 * (`Number.isNaN(Date.parse(value))`) ⇒ neutral. UTC-anchored for determinism
 * (mirrors the SCRUM-172 ownershipDates doctrine — no timezone drift). */
export function formatIsoInstant(value: unknown, intl: IntlShape): string {
  // Require an explicit zone (`Z`/offset) BEFORE parsing — a timezone-less
  // string would parse in the host zone and render host-dependently (codex F-01).
  if (typeof value !== 'string' || !ISO_INSTANT_WITH_ZONE.test(value)) {
    return neutralValue(intl);
  }
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return neutralValue(intl);
  return intl.formatDate(ms, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** Local date: a `YYYY-MM-DD` STRING → localized calendar date (no time). Fail-
 * closed: rejects any string not matching a strict `^\d{4}-\d{2}-\d{2}$` AND not
 * a real calendar date (e.g. `2025-02-29` in a non-leap year, `2025-13-01`) ⇒
 * neutral. UTC-anchored so the day never drifts across the local-timezone boundary. */
export function formatLocalDate(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'string' || !LOCAL_DATE.test(value)) return neutralValue(intl);
  // Real-calendar-date guard: a `Date` built from the parts must round-trip the
  // input (rejects 2025-02-29 / 2025-13-01 / 2025-01-32, which JS would roll over).
  const [y, m, d] = value.split('-').map((p) => Number.parseInt(p, 10));
  const dt = new Date(Date.UTC(y as number, (m as number) - 1, d as number));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== (m as number) - 1 ||
    dt.getUTCDate() !== d
  ) {
    return neutralValue(intl);
  }
  return intl.formatDate(dt, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Enum: string member → its localized label. Unknown member / non-string ⇒ neutral. */
export function formatEnum(value: unknown, intl: IntlShape, enumLabelIds: Readonly<Record<string, string>>): string {
  if (typeof value !== 'string' || !Object.prototype.hasOwnProperty.call(enumLabelIds, value)) {
    return neutralValue(intl);
  }
  const id = enumLabelIds[value];
  // defaultMessage guards a missing catalog entry so the raw id never leaks.
  return intl.formatMessage({ id, defaultMessage: value });
}

/** Time-of-day: a validated "HH:mm" string, rendered verbatim. Malformed ⇒ neutral. */
export function formatTimeOfDay(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'string' || !TIME_OF_DAY.test(value)) return neutralValue(intl);
  return value;
}

/** Count / integer quantity → localized number. Non-finite ⇒ neutral. */
export function formatCount(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return neutralValue(intl);
  return intl.formatNumber(value);
}

/** Geo coordinate (lat/long) → fixed-precision localized number. Non-finite ⇒ neutral. */
export function formatCoordinate(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return neutralValue(intl);
  return intl.formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

/** Boolean → Yes / No. Non-boolean ⇒ neutral. */
export function formatBoolean(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'boolean') return neutralValue(intl);
  return intl.formatMessage({ id: value ? H.booleanTrue : H.booleanFalse });
}

/** Free text → the string itself (already user content). Non-string ⇒ neutral.
 * react-intl / React escape on render, so injection-shaped text is inert. */
export function formatText(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'string') return neutralValue(intl);
  return value;
}

/** Opaque id → the id string verbatim. Non-string ⇒ neutral. */
export function formatId(value: unknown, intl: IntlShape): string {
  if (typeof value !== 'string' || value.length === 0) return neutralValue(intl);
  return value;
}

/** Structured value (e.g. bed configurations) → a generic "updated" marker.
 * We acknowledge the change without serializing the structure (display-only). */
export function formatJson(_value: unknown, intl: IntlShape): string {
  return intl.formatMessage({ id: H.valueComplex });
}

const FORMATTERS: Record<ValueKind, (value: unknown, intl: IntlShape, currency?: string) => string> = {
  enum: () => '', // handled specially (needs enum label ids) — see formatValue
  cents: (v, intl, currency) => formatCents(v, intl, currency),
  instant: (v, intl) => formatInstant(v, intl),
  isoInstant: (v, intl) => formatIsoInstant(v, intl),
  localDate: (v, intl) => formatLocalDate(v, intl),
  timeOfDay: (v, intl) => formatTimeOfDay(v, intl),
  count: (v, intl) => formatCount(v, intl),
  coordinate: (v, intl) => formatCoordinate(v, intl),
  boolean: (v, intl) => formatBoolean(v, intl),
  text: (v, intl) => formatText(v, intl),
  id: (v, intl) => formatId(v, intl),
  json: (v, intl) => formatJson(v, intl),
};

/**
 * Dispatch a single value through the formatter for its kind. `enum` requires
 * the per-field enum label ids and is handled by the caller (the registry knows
 * them); calling `formatValue` with `enum` and no ids returns neutral.
 */
export function formatValue(
  kind: ValueKind,
  value: unknown,
  intl: IntlShape,
  opts?: { currency?: string; enumLabelIds?: Readonly<Record<string, string>> },
): string {
  if (kind === 'enum') {
    return opts?.enumLabelIds ? formatEnum(value, intl, opts.enumLabelIds) : neutralValue(intl);
  }
  return FORMATTERS[kind](value, intl, opts?.currency);
}
